import React from 'react';

// Dil seçenekleri
const LANGUAGE_OPTIONS = [
  { code: 'tr', name: 'Türkçe' },
  { code: 'en', name: 'İngilizce' },
  { code: 'en-US', name: 'Amerikan İngilizcesi' }
];

interface FileUploadProps {
  selectedFile: File | null;
  uploadProgress: number;
  statusMessage: string;
  isDragging: boolean;
  isUploading: boolean;
  showTranslateSuccessModal: boolean;
  currentDocumentId: string | null;
  reportType: string | null;
  reportYear: string | null;
  showConfigModal: boolean;
  fileName: string;
  sourceLanguage: string;
  destinationLanguage: string;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: (event: React.DragEvent<HTMLDivElement>) => void;
  onUpload: () => void;
  onCancel: () => void;
  onBackToOptions: () => void;
  onGoToTranslations: () => void;
  onCopyDocumentId: (documentId: string) => void;
  onRemoveFile: () => void;
  onReportTypeChange: (value: string) => void;
  onReportYearChange: (value: string) => void;
  onContinue: () => void;
  onConfigModalClose: () => void;
  onFileNameChange: (value: string) => void;
  onSourceLanguageChange: (value: string) => void;
  onDestinationLanguageChange: (value: string) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  selectedFile,
  uploadProgress,
  statusMessage,
  isDragging,
  isUploading,
  showTranslateSuccessModal,
  currentDocumentId,
  reportType,
  reportYear,
  showConfigModal,
  fileName,
  sourceLanguage,
  destinationLanguage,
  onFileChange,
  onDrop,
  onDragOver,
  onDragLeave,
  onUpload,
  onCancel,
  onBackToOptions,
  onGoToTranslations,
  onCopyDocumentId,
  onRemoveFile,
  onReportTypeChange,
  onReportYearChange,
  onContinue,
  onConfigModalClose,
  onFileNameChange,
  onSourceLanguageChange,
  onDestinationLanguageChange
}) => {
  return (
    <div className="w-full">
      <div className="px-6">
        {/* Basit Dosya Yükleme Alanı - Tablo göründüğünde veya başarı mesajı gösterilirken gizle */}
        {!showTranslateSuccessModal && (
          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-8 mb-8 max-w-4xl mx-auto">
            {/* Geri Dönüş Butonu */}
            <div className="mb-6">
              <button
                onClick={onBackToOptions}
                className="flex items-center text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors group"
              >
                <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/>
                </svg>
                <span className="text-sm font-medium">İşlem Seçimine Dön</span>
              </button>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
              Dosya Yükle ve Ayrıştır
            </h2>
            
            {/* Dosya Seçme Alanı */}
            <div
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 ${
                isDragging 
                  ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
              }`}
            >
              <div className="flex flex-col items-center space-y-4">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                  </svg>
                </div>
                
                <div>
                  <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Dosyanızı buraya sürükleyin veya seçin
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    DOCX dosyaları desteklenir
                  </p>
                </div>
                
                <label className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg cursor-pointer transition-colors font-medium">
                  <input type="file" className="hidden" onChange={onFileChange} />
                  Dosya Seç
                </label>
              </div>
            </div>


            {/* Seçilen Dosya Gösterimi */}
            {selectedFile && (
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{selectedFile.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button 
                    onClick={onRemoveFile}
                    className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* İlerleme Çubuğu */}
            {isUploading && (
              <div className="mt-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Dosya Ayrıştırılıyor...
                  </span>
                  <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                    {uploadProgress}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Durum Mesajı */}
            {statusMessage && (
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-200">{statusMessage}</p>
              </div>
            )}

            {/* Devam Et Butonu */}
            <div className="mt-6 flex gap-4">
              <button
                onClick={onContinue}
                disabled={!selectedFile}
                className={`flex-1 py-3 px-6 rounded-lg font-medium transition-all duration-300 ${
                  !selectedFile
                    ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                }`}
              >
                Devam Et
              </button>
            </div>
          </div>
        )}

        {/* Başarı Mesajı */}
        {showTranslateSuccessModal && (
          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 p-12 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Çeviri İşleme Alındı
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 mb-4 max-w-2xl mx-auto">
                Çeviriniz işleme alınmıştır. Çevirinizin durumuna "Çevirileri Görüntüle" ekranından ulaşabilirsiniz.
              </p>
              {currentDocumentId && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-8 max-w-2xl mx-auto">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                    Döküman ID:
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="text-lg font-mono text-blue-900 dark:text-blue-100 break-all flex-1">
                      {currentDocumentId}
                    </p>
                    <button
                      onClick={() => onCopyDocumentId(currentDocumentId)}
                      className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-800/20 rounded-lg transition-colors flex-shrink-0"
                      title="Kopyala"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                      </svg>
                    </button>
                  </div>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={onGoToTranslations}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  Çevirileri Görüntüle
                </button>
                <button
                  onClick={onBackToOptions}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-8 py-3 rounded-lg font-medium transition-colors"
                >
                  İşlem Seçimine Dön
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Konfigürasyon Modalı */}
        {showConfigModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    Çeviri Ayarları
                  </h3>
                  <button
                    onClick={onConfigModalClose}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Dosya İsmi Düzenleme */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                        Dosya İsmi
                      </span>
                    </label>
                    <input
                      type="text"
                      value={fileName}
                      onChange={(e) => onFileNameChange(e.target.value.slice(0, 50))}
                      maxLength={50}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                      placeholder="Dosya ismini girin..."
                    />
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right">
                      {fileName.length}/50
                    </div>
                  </div>

                  {/* Rapor Tipi Seçimi */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                        Rapor Tipi
                      </span>
                    </label>
                    <div className="relative">
                      <select
                        value={reportType || ''}
                        onChange={(e) => onReportTypeChange(e.target.value)}
                        className="w-full px-4 py-3 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 appearance-none cursor-pointer hover:border-blue-400 dark:hover:border-blue-400"
                      >
                        <option value="" disabled className="text-gray-500">Rapor türü seçiniz...</option>
                        <option value="entegre-faaliyet">Entegre Faaliyet Raporu</option>
                        <option value="faaliyet">Faaliyet Raporu</option>
                        <option value="surdurulebilirlik">Sürdürülebilirlik Raporu</option>
                        <option value="tsrs">TSRS Raporu</option>
                        <option value="diger">Diğer</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Rapor Yılı Seçimi */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                        </svg>
                        Rapor Yılı
                      </span>
                    </label>
                    <div className="relative">
                      <select
                        value={reportYear || ''}
                        onChange={(e) => onReportYearChange(e.target.value)}
                        className="w-full px-4 py-3 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 appearance-none cursor-pointer hover:border-blue-400 dark:hover:border-blue-400"
                      >
                        <option value="" disabled className="text-gray-500">Yıl seçiniz...</option>
                        {[2025, 2024, 2023, 2022].map((year) => (
                          <option 
                            key={year} 
                            value={year.toString()}
                          >
                            {year}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Kaynak Dil Seçimi */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"/>
                        </svg>
                        Kaynak Dil
                      </span>
                    </label>
                    <div className="relative">
                      <select
                        value={sourceLanguage}
                        onChange={(e) => onSourceLanguageChange(e.target.value)}
                        className="w-full px-4 py-3 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 appearance-none cursor-pointer hover:border-green-400 dark:hover:border-green-400"
                      >
                        {LANGUAGE_OPTIONS.map((lang) => (
                          <option key={lang.code} value={lang.code}>
                            {lang.name}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Hedef Dil Seçimi */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <span className="flex items-center">
                        <svg className="w-4 h-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"/>
                        </svg>
                        Hedef Dil
                      </span>
                    </label>
                    <div className="relative">
                      <select
                        value={destinationLanguage}
                        onChange={(e) => onDestinationLanguageChange(e.target.value)}
                        className="w-full px-4 py-3 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 appearance-none cursor-pointer hover:border-purple-400 dark:hover:border-purple-400"
                      >
                        {LANGUAGE_OPTIONS.map((lang) => (
                          <option key={lang.code} value={lang.code}>
                            {lang.name}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Modal Butonları */}
                <div className="mt-8 flex gap-3">
                  <button
                    onClick={onConfigModalClose}
                    className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
                  >
                    İptal
                  </button>
                  <button
                    onClick={() => {
                      // Dil seçeneklerini JSON formatında konsola yazdır
                      const translationConfig = {
                        source: sourceLanguage,
                        destination: destinationLanguage
                      };
                      console.log('Çeviri Ayarları:', translationConfig);
                      
                      onConfigModalClose();
                      onUpload();
                    }}
                    disabled={!fileName.trim()}
                    className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                      !fileName.trim()
                        ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    Dosya Ayrıştır
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Yükleme Durumu Modalı */}
        {isUploading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="animate-spin w-8 h-8 text-blue-600 dark:text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Dosya Ayrıştırılıyor
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Lütfen bekleyin...
                </p>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <div className="text-sm text-blue-600 dark:text-blue-400 mb-4">
                  {uploadProgress}%
                </div>
                {statusMessage && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {statusMessage}
                  </p>
                )}
                <button
                  onClick={onCancel}
                  className="px-6 py-2 border-2 border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg font-medium transition-colors"
                >
                  İptal
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUpload;
